/**
 * Team leaderboard page — fetches from `/public/leaderboard/:eventId/team`,
 * displays ranked team list with scores, and updates in real-time via WebSocket.
 *
 * Requirements: 16.1 (public leaderboard), 16.2 (team view), 16.3 (real-time updates)
 */

import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { TeamLeaderboardEntry } from '@scoring/shared';
import { usePublicApiClient } from '../hooks/usePublicApiClient.js';
import { useLeaderboardSocket } from '../hooks/useLeaderboardSocket.js';
import type { LeaderboardUpdateMessage } from '../hooks/useLeaderboardSocket.js';

export function TeamLeaderboardPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const apiClient = usePublicApiClient();

  const [entries, setEntries] = useState<TeamLeaderboardEntry[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track which team IDs were recently updated for highlight animation.
  const [updatedTeamIds, setUpdatedTeamIds] = useState<Set<string>>(new Set());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial data fetch.
  useEffect(() => {
    if (!eventId) return;

    let cancelled = false;

    async function fetchData(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.request<{
          entries: TeamLeaderboardEntry[];
          lastUpdated: string;
        }>('GET', `/public/leaderboard/${encodeURIComponent(eventId!)}/team`, {
          public: true,
        });
        if (!cancelled) {
          setEntries(response.entries);
          setLastUpdated(response.lastUpdated);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [eventId, apiClient]);

  // Handle WebSocket updates.
  const handleUpdate = useCallback(
    (message: LeaderboardUpdateMessage) => {
      if (message.eventId !== eventId) return;

      // Determine which teams changed scores.
      const previousScores = new Map(entries.map((e) => [e.teamId, e.totalScore]));
      const changedIds = new Set<string>();
      for (const entry of message.team) {
        const prev = previousScores.get(entry.teamId);
        if (prev !== undefined && prev !== entry.totalScore) {
          changedIds.add(entry.teamId);
        } else if (prev === undefined) {
          // New team appeared.
          changedIds.add(entry.teamId);
        }
      }

      setEntries(message.team);
      setLastUpdated(message.lastUpdated);

      // Trigger highlight animation for changed entries.
      if (changedIds.size > 0) {
        setUpdatedTeamIds(changedIds);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setUpdatedTeamIds(new Set());
          highlightTimeoutRef.current = null;
        }, 2000);
      }
    },
    [eventId, entries],
  );

  useLeaderboardSocket({
    eventId,
    onUpdate: handleUpdate,
    enabled: !loading && !error,
  });

  // Cleanup highlight timeout on unmount.
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="leaderboard-container">
        <p className="loading-message">Loading team leaderboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-container">
        <p className="error-message" role="alert">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <header className="leaderboard-header">
        <h1>Team Leaderboard</h1>
        <nav aria-label="Leaderboard views">
          <Link to={`/${eventId}`} className="nav-link">
            Overview
          </Link>
          <Link to={`/${eventId}/individual`} className="nav-link">
            Individual
          </Link>
        </nav>
      </header>

      {lastUpdated && (
        <p className="last-updated">
          Last updated: {new Date(lastUpdated).toLocaleTimeString()}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="empty-message">No team scores recorded yet.</p>
      ) : (
        <table className="leaderboard-table" aria-label="Team leaderboard rankings">
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Team</th>
              <th scope="col">Score</th>
              <th scope="col">Members</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr
                key={entry.teamId}
                className={
                  updatedTeamIds.has(entry.teamId)
                    ? 'leaderboard-row updated'
                    : 'leaderboard-row'
                }
              >
                <td className="rank-cell">{entry.rank}</td>
                <td className="name-cell">{entry.teamName}</td>
                <td className="score-cell">{entry.totalScore}</td>
                <td className="count-cell">{entry.participantCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
